import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALFINANCE_LOAD_BY_TRIAL_ID,
    ACTION_TRIALFINANCE_LOAD_BY_ID,
    ACTION_TRIALFINANCE_LOAD_FAILURE,
    ACTION_TRIALFINANCE_LOAD_SUCCESS,

    ACTION_TRIALFINANCE_SAVE,
    ACTION_TRIALFINANCE_SAVE_SUCCESS,
    ACTION_TRIALFINANCE_SAVE_FAILURE,

    ACTION_TRIALFINANCE_VALIDATE,
    ACTION_TRIALFINANCE_VALIDATE_SUCCESS,
    ACTION_TRIALFINANCE_VALIDATE_FAILURE,

    ACTION_TRIALFINANCE_CREATE,
    ACTION_TRIALFINANCE_CREATE_SUCCESS,
    ACTION_TRIALFINANCE_CREATE_FAILURE,

    ACTION_TRIALFINANCE_SYNC,
    ACTION_TRIALFINANCE_SYNC_SUCCESS,
    ACTION_TRIALFINANCE_SYNC_FAILURE,

    ACTION_TRIALFINANCE_CLEAR,
    ACTION_TRIALFINANCE_FILTER,

    TrialFinanceAction
} from '../actions/trialFinance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialFinanceState {
    formData: Dtos.TrialFinance | Dtos.TrialFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialFinance[];
    formLength: number;
}

const initialState: ITrialFinanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialFinanceState, _isLoading: boolean): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialFinanceState,
    formData: Dtos.TrialFinance | Dtos.TrialFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialFinanceState, responseStatus: Dtos.ResponseStatus): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialFinanceState, formFilter: IFormFilter): ITrialFinanceState {

    const items: Dtos.TrialFinance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.ctaClauseReference.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialFinanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialFinanceState, propertiesToClear?: string[]): ITrialFinanceState {

    if (!propertiesToClear) {
        const newState: ITrialFinanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialFinanceState, _isSaving: boolean): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialFinanceState,
    formData: Dtos.TrialFinance | Dtos.TrialFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialFinanceState, responseStatus: Dtos.ResponseStatus): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialFinanceState, _isValidating: boolean): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialFinanceState,
    formData: Dtos.TrialFinance | Dtos.TrialFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialFinanceState, responseStatus: Dtos.ResponseStatus): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialFinanceState, _isCreating: boolean): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialFinanceState,
    formData: Dtos.TrialFinance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialFinanceState, responseStatus: Dtos.ResponseStatus): ITrialFinanceState {
    const newState: ITrialFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialFinanceReducer: Reducer<ITrialFinanceState> = (state: ITrialFinanceState = initialState, action: TrialFinanceAction) => {
    switch (action.type) {
        case ACTION_TRIALFINANCE_LOAD_BY_TRIAL_ID:
        case ACTION_TRIALFINANCE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALFINANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALFINANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALFINANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALFINANCE_SAVE:
            return saving(state, true);
        case ACTION_TRIALFINANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALFINANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALFINANCE_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALFINANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALFINANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALFINANCE_CREATE:
            return creating(state, true);
        case ACTION_TRIALFINANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALFINANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALFINANCE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALFINANCE_SYNC:
            return loading(state, true);
        case ACTION_TRIALFINANCE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALFINANCE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialFinanceReducer;